import React from 'react';

const CVComponent = () => {
  return (
    <div className="bg-gray-100 min-h-screen p-4">
      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-700 text-white p-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">OMAR ETKAIDEK</h1>
            <h2 className="text-lg">App Developer & System Designer</h2>
            <p className="text-sm mt-1">Ramallah, Palestine</p>
          </div>
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white">
            <img 
              src="/api/placeholder/150/150" 
              alt="Profile" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Main Content */}
        <div className="p-4">
          {/* Profile */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">PROFILE</h3>
            <p className="text-sm text-gray-700">
              Computer engineering graduate specializing in application development, system design, and database architecture. Experienced in hardware troubleshooting, computer/printer setup, and operating system maintenance. Combines strong technical foundation with international exposure.
            </p>
          </section>
          
          {/* Education */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">EDUCATION</h3>
            <div className="flex justify-between items-start text-sm">
              <p><span className="font-semibold">BSc Computer Engineering</span> - Birzeit University (2024)</p>
              <p>GPA: 85.4%</p>
            </div>
            <p className="text-sm text-gray-700 mt-1">
              Multiple-time Dean's Honor List Recipient
            </p>
            <p className="text-sm text-gray-700">
              Erasmus+ Scholarship Program (6 months in Italy)
            </p>
          </section>
          
          {/* Expertise */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">EXPERTISE</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="font-semibold">App Development</p>
                <p className="text-gray-600">Node.js, Express, Flutter, React, Python</p>
              </div>
              <div>
                <p className="font-semibold">System Design</p>
                <p className="text-gray-600">Architecture planning, security implementation</p>
              </div>
              <div>
                <p className="font-semibold">Database Development</p>
                <p className="text-gray-600">MySQL, NoSQL, Firebase, schema optimization</p>
              </div>
              <div>
                <p className="font-semibold">IT Support</p>
                <p className="text-gray-600">Computer/printer setup, OS troubleshooting</p>
              </div>
            </div>
          </section>
          
          {/* Database Experience Section */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">DATABASE EXPERTISE</h3>
            <div className="text-sm text-gray-700">
              <p className="mb-2">Experienced database developer with comprehensive knowledge of SQL and NoSQL systems. Proficient in database architecture, schema optimization, and integration with modern applications.</p>
              
              <div className="grid grid-cols-1 gap-2 mt-3">
                <div>
                  <p className="font-semibold">Fariqi E-commerce App</p>
                  <p className="text-gray-600">Implemented NoSQL database structure optimized for product catalogs, user profiles, and order processing. Designed efficient data relationships for seamless inventory management.</p>
                </div>
                
                <div>
                  <p className="font-semibold">Car Wash Booking System</p>
                  <p className="text-gray-600">Developed Firebase Realtime Database integration with comprehensive document structure for booking management, user authentication, and service tracking.</p>
                </div>
                
                <div>
                  <p className="font-semibold">Library Management System</p>
                  <p className="text-gray-600">Architected relational database with normalized schema for book inventory, member records, and circulation management. Implemented complex queries for reporting and analytics.</p>
                </div>
              </div>
              
              <div className="flex justify-between mt-3">
                <div>
                  <p className="font-semibold">Technologies:</p>
                  <p className="text-gray-600">MySQL, MongoDB, Firebase, SQLite</p>
                </div>
                <div>
                  <p className="font-semibold">Skills:</p>
                  <p className="text-gray-600">Schema design, query optimization, data modeling, backup systems</p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Network Experience Section */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">NETWORK EXPERIENCE</h3>
            <div className="text-sm text-gray-700">
              <p className="mb-2">Proficient in data communication network principles with solid academic foundation and practical implementation experience.</p>
              
              <div className="grid grid-cols-1 gap-2 mt-3">
                <div>
                  <p className="font-semibold">Network Protocols & Architecture</p>
                  <p className="text-gray-600">Comprehensive understanding of layered network models (OSI, TCP/IP), protocol implementation, and routing methodologies. Experience with error detection/correction mechanisms and ARQ strategies.</p>
                </div>
                
                <div>
                  <p className="font-semibold">LAN/WAN Technologies</p>
                  <p className="text-gray-600">Knowledge of local and wide area network design principles including topology selection, bridge implementation, and internetworking. Applied experience in network traffic management and queuing systems.</p>
                </div>
                
                <div>
                  <p className="font-semibold">Academic Project</p>
                  <p className="text-gray-600">Simulated multiple access communication systems with analysis of throughput and latency metrics. Implemented basic routing algorithms and flow control mechanisms in a test environment.</p>
                </div>
              </div>
              
              <div className="flex justify-between mt-3">
                <div>
                  <p className="font-semibold">Course Coverage:</p>
                  <p className="text-gray-600">Transport protocols, network addressing, M/M/1 queuing system, error detection</p>
                </div>
                <div>
                  <p className="font-semibold">Practical Skills:</p>
                  <p className="text-gray-600">Network configuration, troubleshooting, performance analysis</p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Technical Skills */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">TECHNICAL SKILLS</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="font-semibold">Programming Languages</p>
                <p className="text-gray-600">JavaScript, Python, Dart</p>
              </div>
              <div>
                <p className="font-semibold">Hardware</p>
                <p className="text-gray-600">PC assembly/repair, printer setup & maintenance</p>
              </div>
              <div>
                <p className="font-semibold">Operating Systems</p>
                <p className="text-gray-600">Windows, Linux, macOS configuration & troubleshooting</p>
              </div>
              <div>
                <p className="font-semibold">Security</p>
                <p className="text-gray-600">JWT implementation, authentication systems</p>
              </div>
            </div>
          </section>
          
          {/* Professional Experience & Projects */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">EXPERIENCE & PROJECTS</h3>
            
            <div className="mb-3 text-sm">
              <p className="font-semibold">Backend Developer Internship - ASAL Technology</p>
              <p className="text-gray-600">Node.js/Express backend systems, RESTful APIs, JWT authentication</p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="font-semibold">Fariqi E-commerce App</p>
                <p className="text-gray-600">Flutter/NoSQL app with secure payment processing</p>
              </div>
              <div>
                <p className="font-semibold">Car Wash Booking System</p>
                <p className="text-gray-600">Flutter/Firebase app with comprehensive database</p>
              </div>
              <div>
                <p className="font-semibold">Library Management System</p>
                <p className="text-gray-600">Complete database design with optimized queries</p>
              </div>
              <div>
                <p className="font-semibold">Sleep Stage Classification</p>
                <p className="text-gray-600">Python ML system for EEG analysis</p>
              </div>
            </div>
          </section>
          
          {/* Conferences & Certifications */}
          <section className="mb-4">
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">PROFESSIONAL DEVELOPMENT</h3>
            <ul className="list-disc pl-6 text-sm text-gray-700">
              <li>IT Exhibition (Dubai, October 2024)</li>
              <li>IT Security Conference (Italy, 2023)</li>
              <li>Palestinian Engineers Association Member</li>
              <li>Access Microscholarship Program (English, 2 years)</li>
            </ul>
          </section>
          
          {/* Languages & Attributes */}
          <section>
            <h3 className="text-lg font-bold text-blue-700 border-b border-blue-700 pb-1 mb-2">SKILLS & LANGUAGES</h3>
            <div className="flex justify-between text-sm">
              <div>
                <p className="font-semibold">Languages:</p>
                <p className="text-gray-600">Arabic (Native), English (Professional), Italian (Basic)</p>
              </div>
              <div>
                <p className="font-semibold">Personal:</p>
                <p className="text-gray-600">Problem-solving, leadership, teamwork, communication</p>
              </div>
            </div>
          </section>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-100 p-2 text-center text-xs text-gray-600">
          References available upon request
        </div>
      </div>
    </div>
  );
};

export default CVComponent;